import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Box, 
  Chip,
  Rating,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBookingPath } from '../../constants/routes';
import { truncate } from '../../utils/formatters';
import { addRecentBusiness } from '../../utils/recentBusinesses';

/**
 * Business card component for displaying business summary
 */
const BusinessCard = ({ business }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    id,
    customerId,
    business_name,
    description,
    category,
    rating,
    total_bookings,
    branding,
  } = business;

  const logoUrl = branding?.logo_url;
  const coverImageUrl = branding?.cover_image_url;
  const primaryColor = branding?.primary_color || '#1976d2';

  const handleViewDetails = () => {
    // Save to recent businesses
    addRecentBusiness({
      id,
      customerId,
      business_name,
      description,
      category,
      rating,
      total_bookings,
      branding,
    });
    
    navigate(getBookingPath(id), { 
      state: { 
        customerId,
        business: {
          id,
          customerId,
          business_name,
          description,
          category,
          rating,
          total_bookings,
        }
      } 
    });
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleViewDetails}
    >
      <CardMedia
        component="div"
        sx={{
          height: 140,
          bgcolor: coverImageUrl ? 'transparent' : primaryColor,
          backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {!coverImageUrl && !logoUrl && (
          // Fallback to first letter when no cover or logo
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
              {business_name?.charAt(0)?.toUpperCase() || 'B'}
            </Typography>
          </Box>
        )}
        
        {logoUrl && (
          // Logo circle in top right corner
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3,
              p: 1,
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt={business_name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
      </CardMedia>
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" component="h2" gutterBottom noWrap>
          {business_name}
        </Typography>
        
        {category && (
          <Chip 
            label={category} 
            size="small" 
            sx={{ alignSelf: 'flex-start', mb: 1 }}
          />
        )}
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 2, flexGrow: 1 }}
        >
          {truncate(description, 100)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
          {rating !== undefined && rating !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Rating value={rating} precision={0.5} size="small" readOnly />
              <Typography variant="body2" color="text.secondary">
                ({rating.toFixed(1)})
              </Typography>
            </Box>
          )}
          {total_bookings !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {total_bookings} bookings
            </Typography>
          )}
        </Box>

        <Button 
          variant="contained" 
          fullWidth 
          sx={{ mt: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetails();
          }}
        >
          {t('home.bookAppointment')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BusinessCard;
